import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import CrossService from "../puzzles/crossword/CrossService";
import { findWordInGrid } from "../puzzles/common/utils/PuzzlePlayLogic";
import GridService from "../puzzles/grid/GridService";
import { collectTemplateWordObjsForStoryMask } from "./storyCardMask";
import {
    extractCrosswordPlayInstanceIdFromPayload,
    extractGridPlayInstanceIdFromPayload,
    getHomeStoryCrossPlayInstanceId,
    getHomeStoryGridPlayInstanceId,
    getHomeStoryPuzzleType,
    getHomeStoryTemplateIdForPlayFetch,
    homeStoryHasPlayResolutionCandidate,
} from "./storyRoUtils";

const PREVIEW_GRID_SIZE = 12;

function parseFilledCoordinates(templateOrPuzzle) {
    const raw =
        templateOrPuzzle?.filledCoordinates ??
        templateOrPuzzle?.filled_coordinates ??
        templateOrPuzzle?.template?.filledCoordinates ??
        templateOrPuzzle?.template?.filled_coordinates;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function lettersToGrid(letters, gridSize = PREVIEW_GRID_SIZE) {
    if (!letters || typeof letters !== "string") return [];
    const lines = letters.split("\n").slice(0, gridSize);
    while (lines.length < gridSize) lines.push("");
    return lines.map((line) => {
        const chars = line.split("").map((c) => c.toUpperCase());
        while (chars.length < gridSize) chars.push(" ");
        return chars.slice(0, gridSize);
    });
}

function resolveHiddenMessageCells(hiddenMsg, filledCoordinates) {
    const trimmed = String(hiddenMsg || "").trim();
    if (!trimmed || !filledCoordinates?.length) return [];

    const fromLookup = findWordInGrid(trimmed, filledCoordinates);
    if (fromLookup?.length) return fromLookup;

    const hiddenLower = trimmed.toLowerCase();
    const scattered = filledCoordinates.find(
        (coord) =>
            coord?.direction === "scattered" &&
            String(coord.text || coord.word || "")
                .toLowerCase()
                .trim() === hiddenLower
    );
    if (!scattered?.positions?.length) return [];

    return scattered.positions.map((pos) => ({ row: pos.row, col: pos.col }));
}

function extractPlayablePuzzlePayload(data, type) {
    if (!data || typeof data !== "object") return null;
    if (typeof data.letters === "string" && data.letters.trim()) return data;
    if (type === "CROSSWORD") {
        const nested = data.crossword ?? data.cross;
        if (nested && typeof nested === "object" && nested.letters) return nested;
    } else {
        const nested = data.grid;
        if (nested && typeof nested === "object" && nested.letters) return nested;
    }
    return data.letters ? data : null;
}

/**
 * @param {object} puzzle — GET `/home/cross/:id` or `/home/grid/:id` payload
 * @param {"CROSSWORD"|"WORD_SEARCH"|"HIDDEN_MESSAGE"} type
 */
export function buildWordPuzzleGridPreview(puzzle, type) {
    if (!puzzle?.letters) return null;
    const grid = lettersToGrid(puzzle.letters, PREVIEW_GRID_SIZE);
    if (!grid.length) return null;
    const filledCoordinates = parseFilledCoordinates(puzzle);

    if (type === "CROSSWORD") {
        return { type, grid, filledCoordinates };
    }

    if (type === "HIDDEN_MESSAGE") {
        const hiddenMsg = String(
            puzzle.hiddenMessage ?? puzzle.template?.hiddenMessage ?? ""
        ).trim();
        const hiddenMessageCells = resolveHiddenMessageCells(hiddenMsg, filledCoordinates);
        const hiddenMsgLower = hiddenMsg.toLowerCase();
        const allWords = [];
        filledCoordinates.forEach((word) => {
            const text = String(word.text || word.word || "")
                .toLowerCase()
                .trim();
            if (!text || text === hiddenMsgLower || word.direction === "scattered") return;
            allWords.push(word.text || word.word);
        });
        return { type, grid, filledCoordinates, hiddenMessageCells, allWords };
    }

    return { type: "WORD_SEARCH", grid, filledCoordinates };
}

async function fetchPuzzlePayloadForStoryRow(row, type) {
    if (type === "CROSSWORD") {
        const crossId = getHomeStoryCrossPlayInstanceId(row);
        if (crossId) {
            return CrossService.getPuzzleById(crossId, { isStory: true });
        }
        const tplId = getHomeStoryTemplateIdForPlayFetch(row, type);
        if (!tplId) return null;
        const homePu = await ApiService.getData(`/home/puzzle/${encodeURIComponent(tplId)}`);
        const resolvedId = extractCrosswordPlayInstanceIdFromPayload(homePu);
        if (resolvedId) {
            return CrossService.getPuzzleById(resolvedId, { isStory: true });
        }
        return extractPlayablePuzzlePayload(homePu, type);
    }

    const gridId = getHomeStoryGridPlayInstanceId(row);
    if (gridId) {
        return GridService.getPuzzleById(gridId, { isStory: true });
    }
    const tplId = getHomeStoryTemplateIdForPlayFetch(row, type);
    if (!tplId) return null;
    const homePu = await ApiService.getData(`/home/puzzle/${encodeURIComponent(tplId)}`);
    const resolvedId = extractGridPlayInstanceIdFromPayload(homePu);
    if (resolvedId) {
        return GridService.getPuzzleById(resolvedId, { isStory: true });
    }
    return extractPlayablePuzzlePayload(homePu, type);
}

/** Puzzle lexicon from a play payload (filledCoordinates, wordsInUse) for story underlines. */
export function collectWordObjsFromPuzzlePayload(puzzle) {
    if (!puzzle || typeof puzzle !== "object") return [];
    const tpl = puzzle.template && typeof puzzle.template === "object" ? puzzle.template : {};
    return collectTemplateWordObjsForStoryMask({
        template: tpl,
        filledCoordinates: puzzle.filledCoordinates ?? tpl.filledCoordinates,
        wordsInUse: puzzle.wordsInUse ?? tpl.wordsInUse,
    });
}

/** Loads crossword / grid letters for storybook mini-board preview. */
export async function fetchHomeStoryWordPuzzleGridPreview(row) {
    if (!row || !homeStoryHasPlayResolutionCandidate(row)) return null;
    const type = getHomeStoryPuzzleType(row);
    if (type !== "CROSSWORD" && type !== "WORD_SEARCH" && type !== "HIDDEN_MESSAGE") {
        return null;
    }
    try {
        const payload = await fetchPuzzlePayloadForStoryRow(row, type);
        return buildWordPuzzleGridPreview(payload, type);
    } catch (e) {
        Logger.error("fetchHomeStoryWordPuzzleGridPreview:", e);
        return null;
    }
}

/** Puzzle payload + grid preview for storybook (one fetch). */
export async function fetchHomeStoryWordPuzzleBundle(row) {
    if (!row || !homeStoryHasPlayResolutionCandidate(row)) {
        return { gridPreview: null, wordObjs: [] };
    }
    try {
        const type = getHomeStoryPuzzleType(row);
        const payload = await fetchPuzzlePayloadForStoryRow(row, type);
        return {
            gridPreview: buildWordPuzzleGridPreview(payload, type),
            wordObjs: collectWordObjsFromPuzzlePayload(payload),
        };
    } catch (e) {
        Logger.error("fetchHomeStoryWordPuzzleBundle:", e);
        return { gridPreview: null, wordObjs: [] };
    }
}

/** @typedef {{ type: string, grid: string[][], filledCoordinates: object[], hiddenMessageCells?: object[], allWords?: string[] }} WordPuzzleGridPreview */
